import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { usePublicPlans } from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { formatMoney, formatPercent } from "@/components/pricing/pricingFormat";
import { PageHero } from "@/components/ui/PageHero";
import { Pill } from "@/components/ui/Pill";
import { IngotTable } from "@/ingot";
import { entitlementAvailability } from "@/lib/entitlementAvailability";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { buildTenantUrl } from "@/shell/resolveShell";

/**
 * `/vs-marketplaces` — the comparison a shop owner is already making.
 *
 * Marketplaces compete with a shop for its CUSTOMER, not with us for
 * its software, and a small shop knows them far better than it knows
 * any SaaS. So the page answers the question they actually hold: if I
 * can get work through a marketplace tomorrow, why run my own shop?
 *
 * **Two rules govern what may appear here.**
 *
 * The first is the ticket's: no marketplace is named. We have no
 * documented numbers about any of them, and a comparison built on what
 * we assume a competitor does is a comparison that is wrong the moment
 * they change it. The columns compare two BUSINESS MODELS — selling
 * through someone else's storefront versus your own — which is a fact
 * about the arrangement, not about any company.
 *
 * The second is ours: every row is anchored in something the product
 * does today, and the tier-dependent ones are read from the plan
 * catalogue instead of being written down. Three claims that would
 * have made this page stronger are missing on purpose:
 *
 * - **"The customer never learns we exist."** False below the tier that
 *   grants white-label invoices — every other invoice PDF carries a
 *   Powered-by line, and so does every transactional e-mail.
 * - **"Overflow work goes to a partner under your brand."** There is no
 *   branded handoff to build the claim on; the partner queue is a CSV
 *   and a mailto. And the customer's own traceability dossier prints a
 *   Partner column, so the subcontractor is not hidden either.
 * - **"Take everything and leave."** The export carries configuration
 *   plus B2B customers and their orders. Guest checkouts, invoices and
 *   quotes do not travel, and a page that implied otherwise would be
 *   discovered by exactly the person who trusted it most.
 *
 * The platform fee is stated rather than omitted, and stated first
 * among our own costs. A page that lists someone else's take-rate while
 * quietly leaving out its own is an advert, not a comparison. It is
 * stated per plan, rate and cap as the pricing page prints them
 * (KAN-1954): a "from X % and capped" summary was wrong twice over,
 * since one plan takes nothing and another has no cap.
 */
export default function VsMarketplaces(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  useSeo({
    title: t("vsMarketplaces.seo.title"),
    description: t("vsMarketplaces.seo.description"),
    path: "/vs-marketplaces",
    lang,
  });

  const plans = usePublicPlans();
  const cards = plans.data?.items ?? [];

  // Which plan first opens a custom domain, in that plan's own words.
  // Written into the copy it would survive the repricing that makes it
  // wrong; read from the catalogue it cannot.
  const domainNote = ((): string | null => {
    if (!cards.length) return null;
    const availability = entitlementAvailability(cards, "custom_domain");
    if (availability.kind === "all") return t("vsMarketplaces.domain.all");
    if (availability.kind === "none") return null;
    return t("vsMarketplaces.domain.from", {
      plan: pickI18nBlob(availability.plan.name_i18n, availability.plan.name, availability.plan.name_en, lang),
    });
  })();

  // One line per published plan, read from the same catalogue the
  // pricing page bills from. The cap carries its own currency.
  const feeLines = cards.map((plan) => {
    const name = pickI18nBlob(plan.name_i18n, plan.name, plan.name_en, lang);
    const percent = formatPercent(plan.platform_fee_bps, lang);
    const cap = plan.platform_fee_cap_monthly_minor;
    const text = !plan.platform_fee_bps
      ? t("vsMarketplaces.fee.planNone")
      : cap == null
        ? t("vsMarketplaces.fee.planUncapped", { percent })
        : t("vsMarketplaces.fee.planCapped", {
            percent,
            cap: formatMoney(cap, plan.platform_fee_cap_currency, lang),
          });
    return { tier: plan.tier, name, text };
  });

  // Rows are (question, marketplace, own shop). `note` hangs a derived
  // sentence under the row; a row whose note could not be derived still
  // renders, because the structural claim does not depend on it.
  const rows = ["customer", "price", "money", "invoice", "address", "data"] as const;

  return (
    <>
      <PageHero innerClassName="max-w-5xl px-7 pb-12 pt-12 md:pt-16">
        <Pill tone="accent">{t("vsMarketplaces.eyebrow")}</Pill>
        <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight text-ink md:text-4xl">
          {t("vsMarketplaces.title")}
        </h1>
        <p className="mt-3 max-w-2xl text-ink-2">{t("vsMarketplaces.lead")}</p>
      </PageHero>

      <main className="mx-auto max-w-5xl px-7 pb-20 pt-12">
        {/* Wide content scrolls inside its own box; the page never does.
            ``IngotTable`` rather than a hand-rolled ``<thead>``: there is
            one way to build a table here, and it carries the a11y floor
            (``<th scope>``, caption) with it. */}
        <div className="overflow-x-auto" data-testid="vs-marketplaces-table">
          <IngotTable
            className="min-w-[40rem]"
            caption={t("vsMarketplaces.title")}
            columns={[
              {
                key: "question",
                header: t("vsMarketplaces.colQuestion"),
                cell: (row) => t(`vsMarketplaces.rows.${row}.question`),
                cellClassName: "font-medium text-ink align-top",
              },
              {
                key: "marketplace",
                header: t("vsMarketplaces.colMarketplace"),
                cell: (row) => t(`vsMarketplaces.rows.${row}.marketplace`),
                cellClassName: "text-ink-2 align-top",
              },
              {
                key: "own",
                header: t("vsMarketplaces.colOwn"),
                cell: (row) => (
                  <>
                    {t(`vsMarketplaces.rows.${row}.own`)}
                    {row === "address" && domainNote ? (
                      <span className="mt-1 block text-xs text-ink-3">
                        {domainNote}
                      </span>
                    ) : null}
                  </>
                ),
                cellClassName: "text-ink align-top",
              },
            ]}
            rows={[...rows]}
            rowKey={(row) => row}
            rowTestId={(row) => `vs-marketplaces-row-${row}`}
          />
        </div>

        <section
          className="mt-10 rounded-lg border border-border bg-surface p-5"
          data-testid="vs-marketplaces-fee"
        >
          <h2 className="text-base font-semibold text-ink">
            {t("vsMarketplaces.fee.heading")}
          </h2>
          {feeLines.length ? (
            <>
              <p className="mt-2 text-sm text-ink-2">{t("vsMarketplaces.fee.intro")}</p>
              <ul className="mt-2 space-y-1 text-sm text-ink-2">
                {feeLines.map((line) => (
                  <li key={line.tier} data-testid={`vs-marketplaces-fee-${line.tier}`}>
                    <span className="font-medium text-ink">{line.name}:</span> {line.text}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <p className="mt-2 text-sm text-ink-2">
            {feeLines.length ? t("vsMarketplaces.fee.fair") : t("vsMarketplaces.fee.fallback")}{" "}
            <Link
              to={`/${lang}/pricing-plans`}
              className="underline underline-offset-2 hover:text-ink"
            >
              {t("vsMarketplaces.fee.link")}
            </Link>
          </p>
        </section>

        {/* The "versus Excel and e-mail" comparison is the landing's
            own block and stays there — two tables saying nearly the
            same thing would go out of sync within a release. */}
        <p className="mt-8 text-sm text-ink-2" data-testid="vs-marketplaces-compare-link">
          {t("vsMarketplaces.compareNote")}{" "}
          <Link
            to={`/${lang}#platform-compare`}
            className="underline underline-offset-2 hover:text-ink"
          >
            {t("vsMarketplaces.compareLink")}
          </Link>
        </p>

        <section
          className="mt-14 rounded-xl border border-border bg-surface px-6 py-10 text-center"
          data-testid="vs-marketplaces-cta"
        >
          <h2 className="text-2xl font-semibold tracking-tight text-ink md:text-3xl">
            {t("vsMarketplaces.cta.heading")}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-ink-2">{t("vsMarketplaces.cta.lead")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to={`/${lang}/sign-up-shop`}
              className="inline-flex items-center rounded-md bg-ink px-5 py-2.5 text-sm font-medium text-bg shadow-sm hover:bg-ink-2"
            >
              {t("vsMarketplaces.cta.primary")}
            </Link>
            {/* buildtenanturl-ok: the demo storefront, opened as an
                anonymous shopper. There is no session to carry — the
                whole point is to try it without an account. */}
            <a
              href={buildTenantUrl(`/${lang}`)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-md border border-border-strong bg-bg px-5 py-2.5 text-sm font-medium text-ink hover:bg-surface-2"
              data-testid="vs-marketplaces-demo"
            >
              {t("vsMarketplaces.cta.secondary")}
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
